// src/utils/anonymousSession.js
import { auth, db } from '../firebase/config';
import { signInAnonymously } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

/**
 * Generate browser fingerprint for anonymous session tracking
 * Same algorithm as used in Register.js for consistency
 */
export function generateFingerprint() {
  const timezoneOffset = new Date().getTimezoneOffset();
  const rawFingerprint = `${navigator.userAgent}${window.screen.width}${window.screen.height}${timezoneOffset}`;

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < rawFingerprint.length; i++) {
    const char = rawFingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `fp_${Math.abs(hash).toString(36)}`;
}

/**
 * Check if fingerprint has been used for anonymous session before
 * @param {string} fingerprint - Browser fingerprint
 * @returns {Promise<boolean>} - True if fingerprint has been used
 */
export async function checkFingerprintUsed(fingerprint) {
  try {
    const sessionRef = doc(db, 'anonymousSessions', fingerprint);
    const sessionDoc = await getDoc(sessionRef);

    if (sessionDoc.exists()) {
      console.log('🔍 Fingerprint already used for anonymous session');
      return true;
    }

    console.log('✅ Fingerprint available for new anonymous session');
    return false;
  } catch (error) {
    console.error('❌ Error checking fingerprint:', error);
    // On error, allow session creation (fail open for better UX)
    return false;
  }
}

/**
 * Create new anonymous session with Firebase Anonymous Authentication
 * @param {string} fingerprint - Browser fingerprint
 * @returns {Promise<object>} - Anonymous session object with Firebase user
 */
export async function createAnonymousSession(fingerprint) {
  try {
    console.log('🔐 Creating anonymous session with Firebase Auth...');
    console.log('📍 Fingerprint:', fingerprint);

    // Sign in anonymously with Firebase
    const userCredential = await signInAnonymously(auth);
    const firebaseUser = userCredential.user;

    console.log('✅ Firebase anonymous sign-in successful:', firebaseUser.uid);

    // Create session data
    const sessionData = {
      isAnonymous: true,
      sessionId: fingerprint,
      uid: firebaseUser.uid,  // Firebase anonymous user ID
      credits: 2,
      createdAt: Date.now()
    };

    // Save to localStorage
    localStorage.setItem('anonymousSession', JSON.stringify(sessionData));
    console.log('💾 Anonymous session saved to localStorage:', sessionData);

    // Save to Firestore for tracking
    const firestoreData = {
      fingerprint: fingerprint,
      firebaseUid: firebaseUser.uid,  // Link fingerprint to Firebase UID
      anonymousUserId: firebaseUser.uid,  // Track ownership for security rules
      createdAt: serverTimestamp(),
      conversionCompleted: false,
      registeredUserId: null
    };

    console.log('☁️ Attempting to save to Firestore collection: anonymousSessions/' + fingerprint);
    await setDoc(doc(db, 'anonymousSessions', fingerprint), firestoreData);
    console.log('✅ Anonymous session saved to Firestore successfully');

    return sessionData;
  } catch (error) {
    console.error('❌ Error creating anonymous session:', error);
    console.error('📝 Error code:', error.code);
    console.error('📝 Error message:', error.message);
    console.error('📝 Full error:', JSON.stringify(error, null, 2));
    throw error;
  }
}

/**
 * Get current anonymous session from localStorage
 * @returns {object|null} - Anonymous session object or null
 */
export function getAnonymousSession() {
  try {
    const sessionStr = localStorage.getItem('anonymousSession');
    if (!sessionStr) {
      return null;
    }

    const session = JSON.parse(sessionStr);
    console.log('📖 Anonymous session loaded from localStorage:', session);
    return session;
  } catch (error) {
    console.error('❌ Error reading anonymous session:', error);
    return null;
  }
}

/**
 * Update anonymous session in localStorage
 * @param {object} updates - Fields to update
 */
export function updateAnonymousSession(updates) {
  try {
    const currentSession = getAnonymousSession();
    if (!currentSession) {
      console.warn('⚠️ No anonymous session to update');
      return;
    }

    const updatedSession = {
      ...currentSession,
      ...updates
    };

    localStorage.setItem('anonymousSession', JSON.stringify(updatedSession));
    console.log('✏️ Anonymous session updated in localStorage:', updatedSession);
  } catch (error) {
    console.error('❌ Error updating anonymous session:', error);
  }
}

/**
 * Clear anonymous session from localStorage
 */
export function clearAnonymousSession() {
  try {
    localStorage.removeItem('anonymousSession');
    console.log('🧹 Anonymous session cleared from localStorage');
  } catch (error) {
    console.error('❌ Error clearing anonymous session:', error);
  }
}

/**
 * Mark anonymous session as conversion completed in Firestore
 * @param {string} fingerprint - Browser fingerprint
 * @param {string} transactionId - Transaction ID
 */
export async function markConversionCompleted(fingerprint, transactionId) {
  try {
    const sessionRef = doc(db, 'anonymousSessions', fingerprint);
    await updateDoc(sessionRef, {
      conversionCompleted: true,
      completedAt: serverTimestamp(),
      transactionId: transactionId
    });
    console.log('✅ Anonymous session marked as completed in Firestore');
  } catch (error) {
    console.error('❌ Error marking conversion completed:', error);
    // Don't throw - this is not critical for user experience
  }
}

/**
 * Link anonymous session to registered user
 * @param {string} fingerprint - Browser fingerprint
 * @param {string} userId - Registered user ID
 */
export async function linkToRegisteredUser(fingerprint, userId) {
  try {
    const sessionRef = doc(db, 'anonymousSessions', fingerprint);
    await updateDoc(sessionRef, {
      registeredUserId: userId,
      linkedAt: serverTimestamp()
    });
    console.log('🔗 Anonymous session linked to registered user:', userId);
  } catch (error) {
    console.error('❌ Error linking session to user:', error);
    // Don't throw - this is not critical
  }
}

/**
 * Validate if anonymous user has credits available
 * @param {object} session - Anonymous session object
 * @returns {boolean} - True if credits available
 */
export function hasAnonymousCredits(session) {
  if (!session || !session.isAnonymous) {
    return false;
  }
  return (session.credits || 0) > 0;
}
